import { createSignal } from "solid-js";

/**
 * Whether the browser's sign-in has lapsed.
 *
 * <p>Set when a request comes back 401 while B2C is configured. That combination can only mean the
 * server got no usable identity — the public configurator endpoints are AllowAnonymous and answer 200
 * without a token, so a visitor who never signed in never lands here.</p>
 *
 * <p>Kept in its own module rather than beside the notice component so `api/http.ts` can raise it
 * without importing JSX into the request path.</p>
 */
const [sessionExpired, setSessionExpired] = createSignal(false);

export { sessionExpired };

/** Raise the notice. Idempotent: repeated 401s while it is already showing change nothing. */
export function markSessionExpired() {
  setSessionExpired(true);
}

/** Clear it — for after a successful sign-in, or if a later call succeeds. */
export function clearSessionExpired() {
  setSessionExpired(false);
}

/**
 * The principal the server resolved us as when the session was last loaded.
 *
 * <p>Held here rather than read from `sessionStore` so `api/http.ts` can compare against it: the store
 * imports the API client, which imports `http`, so reaching the other way would be a cycle.</p>
 */
const [knownPrincipal, setKnownPrincipal] = createSignal<string | null>(null);

export { knownPrincipal, setKnownPrincipal };

/**
 * Whether this browser was signed in as a real account when the session last loaded — the server
 * resolved a user row for it.
 *
 * <p>Needed because a 401 does not mean the same thing to everyone. Plenty of endpoints require
 * authentication and are called from pages an anonymous visitor can reach: the postal-code lookup on
 * the summary step is one, and it has always answered 401 for them. Treating that as a lapsed session
 * tells someone who was never signed in that their sign-in expired.</p>
 *
 * <p>Principal alone cannot carry this: a signed-in account with no permissions resolves as Basic,
 * exactly like an anonymous visitor. The user id is what separates them.</p>
 */
const [knownSignedIn, setKnownSignedIn] = createSignal(false);

export { knownSignedIn, setKnownSignedIn };

/**
 * Called when a request comes back 401.
 *
 * <p>Only a lapse for someone who was signed in. Endpoints that require authentication are called from
 * pages an anonymous visitor can reach — the postal-code lookup on the summary step is one, and it has
 * always answered 401 for them — so an ungated notice tells a visitor who never signed in that their
 * sign-in expired.</p>
 */
export function observeUnauthorized() {
  if (knownSignedIn()) markSessionExpired();
}

/**
 * Called with the `X-Session-Principal` header of every response. The server states who it actually
 * served; if that has fallen to `basic` while we were signed in as a dealer or an internal user, the
 * browser's sign-in lapsed and everything since has been answered anonymously — with the dealer's net
 * prices stripped out. Nothing 401s, because the configurator's endpoints allow anonymous callers, so
 * this comparison is the only thing that notices.
 */
export function observeServerPrincipal(served: string | null) {
  if (!served) return;

  const believed = knownPrincipal();
  if (believed && believed !== "basic" && served === "basic") {
    markSessionExpired();
  }
}
